import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

interface UsePreventNavigationOptions {
  when: boolean;
  /** 저장 성공 직후처럼 이미 허용된 이동은 확인 없이 통과시킨다. */
  shouldAllowNavigation?: () => boolean;
  onNavigationAttempt?: (resumeNavigation: () => void) => void;
  onRefreshAttempt?: () => void;
}

/**
 * 페이지 이동 및 새로고침을 조건부로 차단하는 범용 훅
 *
 * @param when - 페이지 이동을 차단할 조건
 * @param onNavigationAttempt - 이동 시도 시 호출되는 콜백. resumeNavigation()을 호출하면 이동 진행
 * @param onRefreshAttempt - 새로고침 시도 시 호출되는 콜백
 *
 * @example
 * // 기본 사용 (isUploading이 true일 때 페이지 이동을 차단)
 * usePreventNavigation({ when: isUploading });
 *
 * @example
 * // 확인모달과 함께 사용
 * usePreventNavigation({
 *   when: isDirty,
 *   onNavigationAttempt: async (resumeNavigation) => {
 *     const confirmed = await confirmDialog.openAsync({ ... });
 *     if (confirmed) resumeNavigation();
 *   },
 * });
 */
export const usePreventNavigation = ({
  when,
  shouldAllowNavigation,
  onNavigationAttempt,
  onRefreshAttempt,
}: UsePreventNavigationOptions) => {
  const router = useRouter();
  const allowNavigationRef = useRef(false);
  const pendingUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (!when || allowNavigationRef.current || shouldAllowNavigation?.())
        return;

      pendingUrlRef.current = url;
      window.history.pushState(null, '', router.asPath);
      router.events.emit('routeChangeError');

      const resumeNavigation = () => {
        allowNavigationRef.current = true;
        if (pendingUrlRef.current) {
          void router.push(pendingUrlRef.current);
        }
      };

      if (onNavigationAttempt) {
        onNavigationAttempt(resumeNavigation);
      }

      // Next.js Pages Router는 routeChangeStart 핸들러에서 던진 *문자열*을
      // 정상적인 라우팅 취소 시그널로 처리한다. Error 객체로 던지면 일반 라우팅 실패로
      // 분류되어 dev overlay/Sentry에 노출되므로 의도적으로 string throw 유지.
      // eslint-disable-next-line zep/no-restricted-syntax -- 위 사유 참고 (Next.js routeChange cancellation 규약)
      throw 'routeChange aborted.';
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router, when, shouldAllowNavigation, onNavigationAttempt]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!when || shouldAllowNavigation?.()) return;
      e.preventDefault();
      onRefreshAttempt?.();
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [when, shouldAllowNavigation, onRefreshAttempt]);
};
